import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { Label } from "layout/typography/Label";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { normaliseBenefitNameSynonyms } from "../../helpers/normaliseBenefitNameSynonyms";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

/**
 * CLE-2661 — the account's default pVerify Service Type.
 *
 * This is the question we ask the payer, not a display preference: the practice type selects which
 * benefit blocks come back at all, so it decides which care setting the copay and co-insurance on
 * every new estimate are read from. An account that bills as a surgery centre and asks the broad
 * combined question can be quoted an office copay.
 *
 * The empty option stores null, which tracks whatever the global default is rather than freezing
 * today's code into the row. It used to repeat that code's full label, which made it read as a
 * duplicate of the explicit "All Other Medical" entry directly beneath it — the two do behave
 * identically today, and the only difference is what happens if the global default ever changes.
 * Named for that behaviour now instead of restating the code.
 */
const practiceTypeOptions = [
  { name: "Not set — follow the system default", value: "" },
  ...mapPayerNameToCodeForDropdown,
];

/**
 * CLE-2661 — which benefit block copay / co-insurance / OOP are read from FIRST.
 *
 * Stored as `Account.serviceCategory`. Labelled by what it does rather than by the enum name: "Clinic"
 * meant nothing to the people who have to set it, and leaving it unset is what makes a narrow practice
 * type dangerous — asking pVerify for Specialist Office while the chain still starts at Hospital
 * Outpatient finds no block and stores a $0 copay.
 *
 * "Not set" is deliberately NOT described as equivalent to Hospital, though both start at Hospital
 * Outpatient. The unset branch carries an extra guard the explicit ones do not: if no SUMMARY block
 * reports a co-insurance it returns null immediately, without consulting the ServiceDetails lines the
 * named branches read first. So an unset account can come back empty where the same response would
 * have produced a value under an explicit setting — which is the argument for setting it.
 *
 * `Lincoln` is a legacy per-account rule set. It is listed, and marked legacy, so that Lincoln's own
 * account round-trips its stored value instead of being silently cleared on the next save — a
 * dropdown cannot display a value that is not among its options. Nothing else should be set to it.
 */
const benefitPriorityOptions = [
  { name: "Not set — general fallback (less reliable)", value: "" },
  { name: "ASC Facility first (surgery centres)", value: "SurgeryCenter" },
  { name: "Hospital Outpatient first (hospitals)", value: "Hospital" },
  {
    name: "Specialist Office first (clinics, in-office specialists)",
    value: "Clinic",
  },
  { name: "Lincoln custom rules (legacy)", value: "Lincoln" },
];

export function Eligibility() {
  const { control } = useFormContext<any>();

  return (
    <div className="flex flex-column gap-3 mt-4">
      <h4 className="m-0">Eligibility</h4>

      <div className="flex flex-column gap-2" style={{ maxWidth: "520px" }}>
        <Label htmlFor="defaultPracticeTypeCode">
          Default Service Type (pVerify practice type)
        </Label>

        <Controller
          control={control}
          name="defaultPracticeTypeCode"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="defaultPracticeTypeCode"
              data-testid="defaultPracticeTypeCode"
              optionLabel="name"
              options={practiceTypeOptions}
              value={value ?? ""}
              onChange={(e) => onChange(e.value === "" ? null : e.value)}
            />
          )}
        />

        <StyledMessage
          severity="info"
          text="Applies to new estimates for this account. Estimates already sent keep the service type they were originally run with."
        />
      </div>

      <div className="flex flex-column gap-2" style={{ maxWidth: "520px" }}>
        <Label htmlFor="serviceCategory">Benefit Priority</Label>

        <Controller
          control={control}
          name="serviceCategory"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="serviceCategory"
              data-testid="serviceCategory"
              optionLabel="name"
              options={benefitPriorityOptions}
              value={value ?? ""}
              onChange={(e) => onChange(e.value === "" ? null : e.value)}
            />
          )}
        />

        <StyledMessage
          severity="info"
          text="Which benefit we read first when the payer returns more than one. Set this to match how the account bills — if it does not match the Service Type above, the estimate can come back with no copay at all. Accounts left unset fall back to a looser rule that is more likely to come back empty."
        />
      </div>

      <div className="flex flex-column gap-2" style={{ maxWidth: "520px" }}>
        <Label htmlFor="benefitNameSynonyms">Health System Names</Label>

        <Controller
          control={control}
          name="benefitNameSynonyms"
          render={({ field: { onChange, value } }) => (
            <Chips
              id="benefitNameSynonyms"
              data-testid="benefitNameSynonyms"
              className="w-full"
              value={value ?? []}
              placeholder="Huntsville Hospital Health System"
              // Normalised with the same rule the API applies before storing, so a chip that will
              // not survive the save is not left sitting on screen looking saved (CLE-2654).
              onChange={(e: ChipsChangeParams) =>
                onChange(normaliseBenefitNameSynonyms(e.value ?? []))
              }
            />
          )}
        />

        <StyledMessage
          severity="info"
          text="If this account belongs to a larger health system, add the name the way the payer writes it. Payers often price their own system differently, and a benefit line naming it is used ahead of the general facility copay. Leave empty if the account has no system affiliation."
        />
      </div>
    </div>
  );
}
